/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * State for the OSD (On-Screen Display) feature slice.
 *
 * The OSD is a branded message overlay shown above full-screen video
 * playback (e.g. inactivity prompts, network/restriction errors). Only one
 * message can be visible at a time; `priority` arbitrates between competing
 * requests so a higher-priority message is never silently clobbered by a
 * lower-priority one while visible.
 *
 * `isRepositionRequired` is fixed per `showOsd()` call — it reflects whether
 * THIS message is a long-lived floating prompt that should periodically
 * reposition to avoid SmartTV screen burn-in (e.g. an inactivity prompt), or
 * a short-lived anchored message that should stay pinned in place (e.g. an
 * error), regardless of whatever banner/overlay is active on screen.
 */
export interface OsdState {
  /** Whether the OSD is currently visible. */
  isVisible: boolean;
  /** Message text currently shown (or last shown) in the OSD. */
  message: string;
  /** Optional short error/status code shown alongside the message. */
  code: string;
  /**
   * Priority of the currently shown (or last shown) message.
   * Higher values win when multiple features request the OSD concurrently.
   */
  priority: number;
  /**
   * Whether the currently shown (or last shown) message should periodically
   * reposition itself at random to prevent SmartTV screen burn-in.
   */
  isRepositionRequired: boolean;
  /**
   * Whether the OSD was hidden by `suspendOsd()` and is eligible to be brought
   * back by `resumeOsd()`.
   *
   * Set when a higher-layer surface (the full-screen dialog) temporarily takes
   * the screen: the OSD is hidden but its message/code/priority are retained so
   * it can be restored verbatim once that surface closes. Distinct from
   * `hideOsd()`, which is a permanent dismissal and resets `priority`.
   */
  isSuspended: boolean;
  /**
   * Country the currently shown (or last shown) message is an Emergency Alert
   * System (EAS) alert for, or `""` when it is not an EAS alert.
   *
   * Non-empty tells `OsdContainer` to render the country-specific `EasOsd`
   * template (via `EasOsdTemplateSelector`) instead of the generic `Osd`
   * component, reusing the same slice/container/priority arbitration rather
   * than a parallel one. Set only via `showEasOsd`, which also fixes
   * `priority` to `EAS_OSD_PRIORITY`.
   */
  easCountry: string;
  /**
   * Whether the Emergency Alert System (EAS) alert on screen carries video —
   * its `audioVideoContent` is `audioVideo` or `videoOnly`.
   *
   * Drives two things that must agree: the alert backdrop goes transparent so
   * the picture is not painted over, and the standalone media element is given
   * a real surface to paint on. `false` for an audio-only alert and for every
   * non-EAS message. Set only via `showEasOsd`.
   */
  easHasVideo: boolean;
  /**
   * Whether the Emergency Alert System (EAS) alert on screen may be dismissed
   * by the viewer.
   *
   * The resolved decision from `EasDismissPolicy`, not the wire's
   * `allowUserExit`: a market whose template shows no dismiss footer is never
   * dismissible. This is the same value `EasInputGate` gates the dismiss key
   * on, and it is the footer's own condition — so what the OSD offers and
   * what the key does are one fact, not two.
   *
   * `false` for every non-EAS message. Set only via `showEasOsd`.
   */
  easIsDismissible: boolean;
  /**
   * A non-EAS message this slice was showing when an EAS alert took the slot,
   * or `null` when the alert displaced nothing.
   *
   * The slice has exactly one slot, so an EAS alert cannot both occupy it and
   * leave what was there intact — `suspendOsd()` is no help, because that
   * hides the slot rather than freeing it. The displaced message is therefore
   * copied aside and put back when the alert ends, so an error the viewer had
   * not dealt with is not silently lost to an emergency.
   */
  displacedOsd: TDisplacedOsd | null;
}

/**
 * A message an EAS alert displaced, held until the alert ends.
 *
 * `isSuspended` is carried so the message is restored to the state it was
 * actually in: a message that was parked behind a full-screen dialog must
 * come back parked, not visible.
 */
export interface TDisplacedOsd {
  /** The displaced message text. */
  message: string;
  /** The displaced message's error/status code. */
  code: string;
  /** The priority it held, so it is restored at its own rank. */
  priority: number;
  /** Whether it repositions itself to prevent burn-in. */
  isRepositionRequired: boolean;
  /** Whether it was parked behind a dialog rather than on screen. */
  isSuspended: boolean;
}

/** Payload for the `showOsd` reducer. */
export interface ShowOsdPayload {
  /** Message text to display. */
  message: string;
  /** Optional short error/status code shown alongside the message. */
  code: string;
  /** Priority of this message request. */
  priority: number;
  /**
   * Whether this message should periodically reposition itself at random to
   * prevent SmartTV screen burn-in — `true` for long-lived floating prompts
   * (e.g. inactivity), `false` for short-lived anchored messages (e.g.
   * errors).
   */
  isRepositionRequired: boolean;
  /**
   * Country this message is an Emergency Alert System (EAS) alert for.
   * Omit (or leave `undefined`) for every non-EAS request — callers should
   * use `showEasOsd` rather than setting this directly. Defaults to `""`.
   */
  easCountry?: string;
  /**
   * Whether this Emergency Alert System (EAS) alert carries video. Omit for
   * every non-EAS request — callers should use `showEasOsd` rather than
   * setting this directly. Defaults to `false`.
   */
  easHasVideo?: boolean;
  /**
   * Whether this Emergency Alert System (EAS) alert may be dismissed by the
   * viewer, as resolved by `EasDismissPolicy`. Omit for every non-EAS request
   * — callers should use `showEasOsd` rather than setting this directly.
   * Defaults to `false`.
   */
  easIsDismissible?: boolean;
}

export const INITIAL_STATE: OsdState = {
  isVisible: false,
  message: "",
  code: "",
  priority: 0,
  isRepositionRequired: false,
  isSuspended: false,
  easCountry: "",
  easHasVideo: false,
  easIsDismissible: false,
  displacedOsd: null
};
